// src/pages/Library.tsx
//
// Mentee-facing Library tab.
//
// DATA SOURCES (all live, no hard-coded content):
//   useGetMyQuarterQuery         — derives personal quarter status for Q1-Q4 selector
//   useGetLibraryContentQuery    — content items for the selected quarter
//   useGetActivityQuery          — which specific library items this mentee has already viewed
//   useGetJourneyQuartersQuery   — contentViewed count per quarter for the progress banner
//   useGetTasksQuery             — task submission status so "Task Brief" cards show progress
//   useLogViewMutation           — fires only once genuine engagement conditions are met
//   useLogDownloadMutation       — fires when a mentee clicks the download button
//
// ─────────────────────────────────────────────────────────────────
// ENGAGEMENT CONDITIONS  (all stringent — opening alone never counts)
// ─────────────────────────────────────────────────────────────────
//
//   KEYNOTE / PANEL (video)
//     • YouTube IFrame API postMessage events are used to track progress
//       because PlyrPlayer exposes no onReady/event callback props.
//     • Per-item watch progress (0–1) is saved to localStorage on every
//       timeupdate message so the user can resume later.
//     • logView fires — once — only when watched seconds ÷ total duration ≥ 80%.
//     • The player bar shows a live "X% watched" indicator while in progress.
//     • Once the threshold is crossed the badge switches to "Watched ✓".
//
//   READING LIST
//     • The panel shows every reading item as a checkbox + reflection textarea.
//     • The user must:
//         1. Tick every item as "read"
//         2. Write a reflection of ≥ MIN_REFLECTION_WORDS words per item
//     • A completion checklist at the bottom shows which conditions remain.
//     • Only when BOTH conditions are met for EVERY item does a
//       "Mark as complete" button become active.
//     • logView fires on that button click — not on panel open.
//     • Ticks + reflections are persisted in localStorage so progress
//       is not lost on close/reopen.
//
//   RESOURCE / TASK BRIEF (document / PDF)
//     • Opening the file link alone does NOT trigger a log.
//     • A two-step confirmation dialog is shown:
//         Step 1 — "Open document"  (enables step 2)
//         Step 2 — "I have read this document in full" (active only after step 1)
//     • logView fires on the step-2 confirmation click.
//
// ─────────────────────────────────────────────────────────────────
// VIDEO MODAL UI FIX
// ─────────────────────────────────────────────────────────────────
//   • The overlay uses inline styles (position:fixed, inset:0, z-index:9999)
//     so it is never clipped by a parent with overflow:hidden or a stacking
//     context that sits below the portal root.
//   • The layout is an explicit flex column: fixed header bar → flex-1 centred
//     player area.  The player wrapper uses a 16:9 padding-bottom trick inside
//     a constrained max-width container so it always fills available space
//     correctly on every screen size.
//   • The overlay is rendered after the main return (outside the page scroll
//     div) so it is never inside a transformed ancestor.
//
// LOCKING RULES  (personal, not global):
//   completed  — full access, all items visible
//   active     — full access
//   upcoming   — teaser: quarter theme shown, content count shown, no item access
//   locked     — completely hidden; mentee is not yet on this quarter

import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Play,
  BookOpen,
  Download,
  FileText,
  Search,
  Lock,
  CheckCircle2,
  Clock,
  ChevronRight,
  Headphones,
  ExternalLink,
  Eye,
  Star,
  X,
  Check,
  AlertCircle,
  Zap,
  TrendingUp,
  MessageSquare,
  Info,
} from "lucide-react";
import {
  ContentType,
  LibraryContent,
  useGetLibraryContentQuery,
  useGetVideoProgressQuery,
  useSaveVideoProgressMutation,
  useLogDownloadMutation,
  useLogViewMutation,
} from "../../app/api/libraryApi";
// import PlyrPlayer from "../../components/common/PlyrPlayer";
import {
  QuarterKey,
  QuarterSummaryItem,
  useGetMyQuarterQuery,
} from "../../app/api/programmeApi";
import {
  ActivityItem,
  useGetActivityQuery,
  useGetJourneyQuartersQuery,
} from "../../app/api/journeyApi";
import { useGetTasksQuery } from "../../app/api/tasksApi";

/* ─────────────────────────────────────────────────────────────────
   ENGAGEMENT THRESHOLDS
───────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────
   DISPLAY CONSTANTS
───────────────────────────────────────────────────────────────── */

type CType = ContentType;

const TYPE_META: Record<
  CType,
  {
    icon: React.ReactNode;
    colour: string;
    bg: string;
    label: string;
    accentBar: string;
  }
> = {
  keynote: {
    icon: <Play size={13} />,
    colour: "text-green-700",
    bg: "bg-green-100",
    label: "Keynote",
    accentBar: "bg-green-400",
  },
  panel: {
    icon: <Headphones size={13} />,
    colour: "text-blue-700",
    bg: "bg-blue-100",
    label: "Panel",
    accentBar: "bg-blue-400",
  },
  reading: {
    icon: <BookOpen size={13} />,
    colour: "text-purple-700",
    bg: "bg-purple-100",
    label: "Reading",
    accentBar: "bg-purple-400",
  },
  resource: {
    icon: <FileText size={13} />,
    colour: "text-orange-700",
    bg: "bg-orange-100",
    label: "Resource",
    accentBar: "bg-orange-400",
  },
  task: {
    icon: <CheckCircle2 size={13} />,
    colour: "text-rose-700",
    bg: "bg-rose-100",
    label: "Task Brief",
    accentBar: "bg-rose-400",
  },
};

const TASK_STATUS_META = {
  not_started: {
    label: "Not started",
    colour: "text-gray-400",
    dot: "bg-gray-300",
  },
  in_progress: {
    label: "In progress",
    colour: "text-blue-600",
    dot: "bg-blue-400",
  },
  submitted: {
    label: "Submitted",
    colour: "text-amber-600",
    dot: "bg-amber-400",
  },
  graded: { label: "Graded", colour: "text-green-600", dot: "bg-green-500" },
} as const;

/* ─────────────────────────────────────────────────────────────────
   ENGAGEMENT THRESHOLDS
───────────────────────────────────────────────────────────────── */

/** Fraction of video duration that must be watched before "Mark as watched" activates */
const VIDEO_COMPLETION_THRESHOLD = 0.8;

/** Minimum words required in the video summary */
const MIN_SUMMARY_WORDS = 30;

/** Minimum words required in each per-item reading reflection */
const MIN_REFLECTION_WORDS = 30;

/* ─────────────────────────────────────────────────────────────────
   LOCAL-STORAGE / SESSION-STORAGE HELPERS
───────────────────────────────────────────────────────────────── */

// localStorage — persists across sessions
const LS_VIDEO_PROGRESS = (id: string) => `lib_vid_pct_${id}`;
const LS_VIDEO_SUMMARY = (id: string) => `lib_vid_summary_${id}`;
const LS_READING_CHECKS = (id: string) => `lib_read_checks_${id}`;
const LS_READING_REFLECTIONS = (id: string) => `lib_read_reflections_${id}`;

// sessionStorage — persists within the tab session (exact seconds for seek-on-resume)
// const SS_VIDEO_SECONDS = (id: string) => `lib_vid_seconds_${id}`;

function lsGet<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function lsSet(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

// function ssGetNumber(key: string, fallback = 0): number {
//   try {
//     const raw = sessionStorage.getItem(key);
//     return raw ? Math.max(0, parseFloat(raw)) : fallback;
//   } catch {
//     return fallback;
//   }
// }
// function ssSetNumber(key: string, value: number) {
//   try {
//     sessionStorage.setItem(key, String(value));
//   } catch {}
// }

const wordCount = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

const extractYouTubeId = (url?: string): string | null => {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  );
  return m ? m[1] : null;
};

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

/* ─────────────────────────────────────────────────────────────────
   SKELETON
───────────────────────────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

/* ─────────────────────────────────────────────────────────────────
   TYPE CHIP
───────────────────────────────────────────────────────────────── */

const TypeChip: React.FC<{ type: CType; small?: boolean }> = ({
  type,
  small,
}) => {
  const m = TYPE_META[type];
  return (
    <span
      className={`inline-flex items-center gap-1 font-medium rounded-md
      ${small ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-1"} ${m.bg} ${m.colour}`}
    >
      {m.icon}
      {m.label}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   VIDEO MODAL
   ─────────────────────────────────────────────────────────────────
   Tracking strategy — same direct Plyr bootstrap as WebinarPage:
     • Creates Plyr with `new (window as any).Plyr(el, {...})` so we
       have access to p.currentTime, p.duration, p.on("playing") etc.
     • A 500 ms setInterval polls p.currentTime / p.duration while
       the video is in the "playing" state (same as WebinarPage).

   Cross-device progress persistence:
     • On modal OPEN  : fetches server-side progress with
       useGetVideoProgressQuery and seeds localStorage + player
       seek position so the user resumes where they left off even
       on a new device.
     • On PAUSE / CLOSE: calls saveVideoProgress (PATCH) to push
       the current fraction and partial summary to the backend.
       Uses a useRef so the cleanup in useEffect can read the latest
       progress without stale closure issues.
     • On "Mark as watched": calls saveVideoProgress with
       { complete: true } which triggers content_viewed creation
       server-side and invalidates journey caches.

   Engagement conditions (BOTH must be met):
     1. User watches ≥ 80 % of the video duration
     2. User writes a summary of ≥ MIN_SUMMARY_WORDS words

   The summary is stored in MenteeActivity.videoSummary so admins
   can view it as engagement evidence in the M&E dashboard.
───────────────────────────────────────────────────────────────── */

interface VideoModalProps {
  item: LibraryContent;
  alreadyDone: boolean;
  onComplete: () => void;
  onClose: () => void;
}

const VideoModal: React.FC<VideoModalProps> = ({
  item,
  alreadyDone,
  onComplete,
  onClose,
}) => {
  const videoId = extractYouTubeId(item.videoUrl);

  // ── Server-side progress fetch — this is the ONLY source of truth ──
  // localStorage is NEVER read as an initial value. It is written as a
  // fast same-session cache but always overridden by the server on mount.
  // This guarantees cross-device consistency: whichever device saved
  // progress to the backend last is what every device resumes from.
  const { data: serverProgress, isLoading: serverLoading } =
    useGetVideoProgressQuery(item.id, {
      skip: alreadyDone,
    });
  const [saveVideoProgress] = useSaveVideoProgressMutation();

  // ── Derived server values (null = not yet loaded) ─────────────────
  // We never initialise from localStorage. State starts at null/empty
  // and is set once (and only once) when the server query resolves.
  const serverSeeded = useRef(false); // ensures the seed runs exactly once

  const [watchedPct, setWatchedPct] = useState<number>(alreadyDone ? 1 : 0);
  const [summary, setSummary] = useState<string>("");
  const [showSummary, setShowSummary] = useState<boolean>(alreadyDone);
  const [completed, setCompleted] = useState<boolean>(alreadyDone);
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Refs for stale-closure safety in Plyr event handlers
  const containerRef = useRef<HTMLDivElement>(null);
  const plyrRef = useRef<any>(null);
  const progressIntervalRef = useRef<number>(-1);
  const completedRef = useRef<boolean>(alreadyDone);
  const watchedPctRef = useRef<number>(alreadyDone ? 1 : 0);
  const summaryRef = useRef<string>("");
  const destroyedRef = useRef<boolean>(false);
  // The exact second position to seek to once Plyr is ready.
  // Set when the server query resolves; Plyr reads it on "ready".
  const resumeSecondsRef = useRef<number>(0);
  // Whether Plyr has been initialised. We defer init until the server
  // query has resolved so the seek target is available immediately.
  const plyrInitialisedRef = useRef<boolean>(false);

  const summaryWc = useMemo(() => wordCount(summary), [summary]);
  const summaryOk = summaryWc >= MIN_SUMMARY_WORDS;
  const thresholdMet = watchedPct >= VIDEO_COMPLETION_THRESHOLD;
  const canMark = thresholdMet && summaryOk && !completed;
  const displayPct = Math.round(watchedPct * 100);
  const thresholdPct = Math.round(VIDEO_COMPLETION_THRESHOLD * 100);

  // ── Plyr helpers ───────────────────────────────────────────────────
  const stopInterval = useCallback(() => {
    if (progressIntervalRef.current !== -1) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = -1;
    }
  }, []);

  const syncProgress = useCallback(() => {
    const p = plyrRef.current;
    if (!p) return;
    try {
      const cur = Number(p.currentTime ?? 0);
      const raw = Number(p.duration) || Number(p.media?.duration) || 0;
      const dur = raw > 0 && isFinite(raw) ? raw : 1;
      const pct = Math.min(cur / dur, 1);
      watchedPctRef.current = pct;
      // Write to localStorage as a fast same-device cache (never read back as initial value)
      lsSet(LS_VIDEO_PROGRESS(item.id), pct);
      setWatchedPct(pct);
      if (!completedRef.current && pct >= VIDEO_COMPLETION_THRESHOLD) {
        setShowSummary(true);
      }
    } catch {}
  }, [item.id]);

  const startInterval = useCallback(() => {
    stopInterval();
    syncProgress();
    progressIntervalRef.current = window.setInterval(syncProgress, 500);
  }, [stopInterval, syncProgress]);

  // ── Push progress to backend ───────────────────────────────────────
  const pushProgress = useCallback(
    async (opts: { complete?: boolean } = {}) => {
      if (completedRef.current && !opts.complete) return;
      try {
        await saveVideoProgress({
          id: item.id,
          progress: watchedPctRef.current,
          summary: summaryRef.current,
          complete: opts.complete ?? false,
        }).unwrap();
      } catch {}
    },
    [saveVideoProgress, item.id],
  );

  // ── Close handler — flush progress first ──────────────────────────
  const handleClose = useCallback(() => {
    stopInterval();
    if (!completedRef.current && watchedPctRef.current > 0) {
      pushProgress(); // fire-and-forget — don't block close
    }
    onClose();
  }, [stopInterval, pushProgress, onClose]);

  // ── Escape key ─────────────────────────────────────────────────────
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Plyr CSS ────────────────────────────────────────────────────────
  useEffect(() => {
    const PLYR_CSS =
      "https://cdnjs.cloudflare.com/ajax/libs/plyr/3.7.8/plyr.min.css";
    if (!document.querySelector(`link[href="${PLYR_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = PLYR_CSS;
      document.head.appendChild(link);
    }
  }, []);

  // ── Core: seed state from server, then boot Plyr ───────────────────
  //
  // DESIGN DECISION: Plyr is not initialised until the server query
  // resolves. This guarantees resumeSecondsRef is set to the correct
  // position before the "ready" event fires, so the seek is always
  // accurate regardless of which device was used last.
  //
  // For already-completed items (alreadyDone=true) the query is skipped
  // and Plyr boots immediately at t=0 (re-watch from beginning).
  //
  // The seeding runs exactly once (serverSeeded ref) even if RTK Query
  // re-renders the component with the same data.
  useEffect(() => {
    if (!videoId) return;

    // ── Case 1: already fully watched — boot Plyr immediately ────────
    if (alreadyDone && !plyrInitialisedRef.current) {
      resumeSecondsRef.current = 0;
      initPlyr();
      return;
    }

    // ── Case 2: server query not yet resolved — wait ──────────────────
    if (serverLoading || !serverProgress?.data) return;

    // ── Case 3: server query resolved — seed state once ──────────────
    if (!serverSeeded.current) {
      serverSeeded.current = true;

      const {
        progress,
        summary: serverSummary,
        complete,
      } = serverProgress.data;

      if (complete) {
        // Marked as watched on another device — open in completed state
        completedRef.current = true;
        watchedPctRef.current = 1;
        summaryRef.current = serverSummary;
        setCompleted(true);
        setWatchedPct(1);
        setShowSummary(true);
        setSummary(serverSummary);
        // Also write to localStorage so subsequent same-device opens are instant
        lsSet(LS_VIDEO_PROGRESS(item.id), 1);
        lsSet(LS_VIDEO_SUMMARY(item.id), serverSummary);
      } else if (progress > 0) {
        // Partially watched on another device — seed progress
        watchedPctRef.current = progress;
        summaryRef.current = serverSummary;
        setWatchedPct(progress);
        setSummary(serverSummary);
        if (progress >= VIDEO_COMPLETION_THRESHOLD) setShowSummary(true);
        // Convert fraction to seconds for the Plyr seek
        // durationMinutes is available from the content item
        resumeSecondsRef.current = progress * (item.durationMinutes ?? 0) * 60;
        // Update localStorage to match server
        lsSet(LS_VIDEO_PROGRESS(item.id), progress);
        lsSet(LS_VIDEO_SUMMARY(item.id), serverSummary);
      }
      // progress === 0 means never watched — resumeSecondsRef stays 0
    }

    // Boot Plyr exactly once after seeding
    if (!plyrInitialisedRef.current) {
      initPlyr();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverProgress, serverLoading, alreadyDone, videoId]);

  // ── Plyr bootstrap ─────────────────────────────────────────────────
  // Extracted into a named function so the seeding useEffect above can
  // call it at the right moment (after the server value is known).
  function initPlyr() {
    if (plyrInitialisedRef.current || destroyedRef.current) return;
    plyrInitialisedRef.current = true;
    destroyedRef.current = false;

    let retryTimer: number;

    const boot = () => {
      if (destroyedRef.current || !containerRef.current) return;

      if (!(window as any).Plyr) {
        if (!document.querySelector('script[src*="plyr.min.js"]')) {
          const s = document.createElement("script");
          s.src =
            "https://cdnjs.cloudflare.com/ajax/libs/plyr/3.7.8/plyr.min.js";
          s.onload = () => {
            if (!destroyedRef.current) boot();
          };
          document.head.appendChild(s);
        } else {
          retryTimer = window.setTimeout(boot, 200);
        }
        return;
      }

      containerRef.current.innerHTML = "";
      const el = document.createElement("div");
      el.setAttribute("data-plyr-provider", "youtube");
      el.setAttribute("data-plyr-embed-id", videoId!);
      containerRef.current.appendChild(el);

      const p = new (window as any).Plyr(el, {
        controls: [
          "play-large",
          "play",
          "progress",
          "current-time",
          "duration",
          "mute",
          "volume",
          "fullscreen",
        ],
        clickToPlay: true,
        keyboard: { global: false, focused: true },
        autoplay: false,
        muted: false,
        youtube: {
          noCookie: true,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          autoplay: 0,
          fs: 0,
          iv_load_policy: 3,
        },
      });

      plyrRef.current = p;

      p.on("ready", () => {
        if (destroyedRef.current) return;
        setIsVideoReady(true);
        // Seek to server-supplied position — computed before Plyr booted
        const targetSecs = resumeSecondsRef.current;
        if (targetSecs > 5) {
          window.setTimeout(() => {
            if (!destroyedRef.current) {
              try {
                p.currentTime = targetSecs;
              } catch {}
            }
          }, 400);
        }
      });

      p.on("playing", () => {
        if (!destroyedRef.current) startInterval();
      });

      p.on("pause", () => {
        if (destroyedRef.current) return;
        syncProgress();
        stopInterval();
        pushProgress();
      });

      p.on("seeked", () => {
        if (!destroyedRef.current) syncProgress();
      });
      p.on("timeupdate", () => {
        if (!destroyedRef.current) syncProgress();
      });

      p.on("ended", () => {
        if (destroyedRef.current) return;
        stopInterval();
        watchedPctRef.current = 1;
        setWatchedPct(1);
        lsSet(LS_VIDEO_PROGRESS(item.id), 1);
        setShowSummary(true);
        pushProgress();
      });

      // Store cleanup callback so the useEffect return can call it
      (p as any).__retryTimer = retryTimer;
    };

    boot();
  }

  // ── Cleanup on unmount ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      destroyedRef.current = true;
      stopInterval();
      // Flush progress to backend on unmount
      if (!completedRef.current && watchedPctRef.current > 0) {
        saveVideoProgress({
          id: item.id,
          progress: watchedPctRef.current,
          summary: summaryRef.current,
        }).catch(() => {});
      }
      try {
        plyrRef.current?.destroy?.();
      } catch {}
      plyrRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Summary handler ────────────────────────────────────────────────
  const handleSummaryChange = (val: string) => {
    setSummary(val);
    summaryRef.current = val;
    lsSet(LS_VIDEO_SUMMARY(item.id), val);
  };

  // ── Mark as watched ────────────────────────────────────────────────
  const handleMarkWatched = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await saveVideoProgress({
        id: item.id,
        progress: watchedPctRef.current,
        summary: summaryRef.current,
        complete: true,
      }).unwrap();
      completedRef.current = true;
      setCompleted(true);
      onComplete();
    } catch (err: any) {
      setSaveError(err?.data?.error ?? "Could not save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          padding: "12px 14px",
          background: "rgba(0,0,0,0.55)",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <TypeChip type={item.contentType} small />
          <p
            style={{
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
              marginTop: 4,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.title}
          </p>
          {item.durationLabel && (
            <p
              style={{
                color: "rgba(255,255,255,0.45)",
                fontSize: 11,
                marginTop: 2,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Clock size={10} /> {item.durationLabel}
            </p>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          {completed ? (
            <span
              style={{
                background: "#16a34a",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 999,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Check size={10} /> Watched ✓
            </span>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 4,
              }}
            >
              <span
                style={{
                  color: "rgba(255,255,255,0.45)",
                  fontSize: 10,
                  whiteSpace: "nowrap",
                }}
              >
                {displayPct}% watched · {thresholdPct}% required
              </span>
              <div
                style={{
                  width: 90,
                  height: 4,
                  background: "rgba(255,255,255,0.12)",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min(100, displayPct)}%`,
                    background:
                      displayPct >= thresholdPct ? "#22c55e" : "#818cf8",
                    borderRadius: 999,
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
            </div>
          )}
          <button
            onClick={handleClose}
            style={{
              padding: 8,
              borderRadius: 10,
              background: "rgba(255,255,255,0.08)",
              border: "none",
              cursor: "pointer",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* ── Body: loading state while server query resolves ──────── */}
      {!alreadyDone && serverLoading && (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.35)",
            fontSize: 13,
            gap: 10,
            flexDirection: "column",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              border: "3px solid rgba(255,255,255,0.1)",
              borderTop: "3px solid #818cf8",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <span>Loading your progress…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── Scrollable body: player + summary ───────────────────── */}
      {(alreadyDone || !serverLoading) && (
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch" as any,
            padding: "18px 12px 28px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 860, margin: "0 auto" }}>
            {videoId ? (
              <>
                {/* Player shell */}
                <div
                  style={{
                    width: "100%",
                    borderRadius: 14,
                    overflow: "hidden",
                    background: "#111",
                    boxShadow: "0 0 0 1px rgba(255,255,255,0.06)",
                  }}
                >
                  {!isVideoReady && (
                    <div
                      style={{
                        aspectRatio: "16 / 9",
                        minHeight: 220,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(255,255,255,0.45)",
                        fontSize: 12,
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          border: "2px solid rgba(255,255,255,0.15)",
                          borderTop: "2px solid rgba(255,255,255,0.6)",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      Loading player…
                    </div>
                  )}
                  <div
                    ref={containerRef}
                    style={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      minHeight: 220,
                      display: isVideoReady ? "block" : "none",
                    }}
                  />
                </div>

                {/* Chapter markers */}
                {(item.chapterMarkers?.length ?? 0) > 0 && (
                  <div
                    style={{
                      marginTop: 12,
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: 10,
                      padding: 12,
                    }}
                  >
                    <p
                      style={{
                        color: "rgba(255,255,255,0.35)",
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        marginBottom: 8,
                      }}
                    >
                      Chapters
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(180px, 1fr))",
                        gap: "6px 16px",
                      }}
                    >
                      {item.chapterMarkers!.map((cm, i) => {
                        const mins = Math.floor(cm.timeSeconds / 60);
                        const secs = cm.timeSeconds % 60;
                        return (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontSize: 11,
                              color: "rgba(255,255,255,0.6)",
                            }}
                          >
                            <span
                              style={{
                                fontFamily: "monospace",
                                color: "rgba(255,255,255,0.3)",
                                flexShrink: 0,
                              }}
                            >
                              {String(mins).padStart(2, "0")}:
                              {String(secs).padStart(2, "0")}
                            </span>
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {cm.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Threshold hint */}
                {!thresholdMet && !completed && isVideoReady && (
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "rgba(255,255,255,0.3)",
                      fontSize: 11,
                    }}
                  >
                    <Info size={12} />
                    Watch at least {thresholdPct}% of this video, then write a
                    short summary to mark it complete.
                  </div>
                )}

                {/* ── Summary panel ── */}
                {(showSummary || completed) && (
                  <div
                    style={{
                      marginTop: 20,
                      background: "rgba(255,255,255,0.06)",
                      borderRadius: 12,
                      padding: 16,
                    }}
                  >
                    {completed ? (
                      <div style={{ textAlign: "center" }}>
                        <p
                          style={{
                            color: "#4ade80",
                            fontWeight: 700,
                            fontSize: 14,
                            marginBottom: 4,
                          }}
                        >
                          ✓ Watched &amp; summarised
                        </p>
                        <p
                          style={{
                            color: "rgba(255,255,255,0.4)",
                            fontSize: 12,
                          }}
                        >
                          Your engagement and summary have been recorded and are
                          visible to your mentor.
                        </p>
                        {summary && (
                          <div
                            style={{
                              marginTop: 12,
                              textAlign: "left",
                              background: "rgba(255,255,255,0.05)",
                              borderRadius: 8,
                              padding: "10px 14px",
                            }}
                          >
                            <p
                              style={{
                                color: "rgba(255,255,255,0.35)",
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                marginBottom: 6,
                              }}
                            >
                              Your summary
                            </p>
                            <p
                              style={{
                                color: "rgba(255,255,255,0.6)",
                                fontSize: 12,
                                lineHeight: 1.6,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {summary}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <p
                          style={{
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: 13,
                            marginBottom: 4,
                          }}
                        >
                          {thresholdMet
                            ? "Write a summary to complete"
                            : "Keep watching…"}
                        </p>
                        <p
                          style={{
                            color: "rgba(255,255,255,0.4)",
                            fontSize: 11,
                            marginBottom: 12,
                            lineHeight: 1.5,
                          }}
                        >
                          {thresholdMet
                            ? `You've watched ${displayPct}% — great! Now write at least ${MIN_SUMMARY_WORDS} words summarising the key lessons. Your summary will be saved to your profile as evidence of engagement.`
                            : `You've watched ${displayPct}% so far. The summary field will activate once you reach ${thresholdPct}%.`}
                        </p>

                        <div style={{ position: "relative" }}>
                          <textarea
                            rows={6}
                            disabled={!thresholdMet}
                            placeholder={
                              thresholdMet
                                ? `What were the key points, lessons, or takeaways from "${item.title}"?\n\nBe specific — your mentor will read this as evidence of your engagement.`
                                : `Available after you've watched ${thresholdPct}% of the video…`
                            }
                            value={summary}
                            onChange={(e) =>
                              handleSummaryChange(e.target.value)
                            }
                            style={{
                              width: "100%",
                              background: thresholdMet
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(255,255,255,0.03)",
                              border: `1px solid ${summaryOk ? "rgba(74,222,128,0.4)" : "rgba(255,255,255,0.1)"}`,
                              borderRadius: 8,
                              padding: "10px 48px 10px 12px",
                              color: thresholdMet
                                ? "rgba(255,255,255,0.85)"
                                : "rgba(255,255,255,0.25)",
                              fontSize: 12,
                              lineHeight: 1.6,
                              resize: "vertical",
                              outline: "none",
                              boxSizing: "border-box" as const,
                              cursor: thresholdMet ? "text" : "not-allowed",
                            }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              bottom: 8,
                              right: 10,
                              fontSize: 10,
                              fontWeight: 700,
                              color: summaryOk
                                ? "#4ade80"
                                : summaryWc > 0
                                  ? "#fbbf24"
                                  : "rgba(255,255,255,0.2)",
                            }}
                          >
                            {summaryWc} / {MIN_SUMMARY_WORDS} words{" "}
                            {summaryOk ? "✓" : ""}
                          </span>
                        </div>

                        <div
                          style={{
                            marginTop: 14,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          {[
                            {
                              ok: thresholdMet,
                              label: `Watch ≥ ${thresholdPct}% of video (${displayPct}% so far)`,
                            },
                            {
                              ok: summaryOk,
                              label: `Write a summary (≥ ${MIN_SUMMARY_WORDS} words, ${summaryWc} written)`,
                            },
                          ].map((c, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontSize: 11,
                              }}
                            >
                              <span
                                style={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: "50%",
                                  background: c.ok
                                    ? "#16a34a"
                                    : "rgba(255,255,255,0.12)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {c.ok && <Check size={9} color="#fff" />}
                              </span>
                              <span
                                style={{
                                  color: c.ok
                                    ? "#4ade80"
                                    : "rgba(255,255,255,0.4)",
                                }}
                              >
                                {c.label}
                              </span>
                            </div>
                          ))}
                        </div>

                        {saveError && (
                          <p
                            style={{
                              marginTop: 8,
                              color: "#f87171",
                              fontSize: 11,
                            }}
                          >
                            {saveError}
                          </p>
                        )}

                        <button
                          disabled={!canMark || isSaving}
                          onClick={handleMarkWatched}
                          style={{
                            marginTop: 14,
                            width: "100%",
                            padding: "10px 0",
                            borderRadius: 10,
                            border: "none",
                            background: canMark
                              ? "#16a34a"
                              : "rgba(255,255,255,0.08)",
                            color: canMark ? "#fff" : "rgba(255,255,255,0.25)",
                            fontSize: 13,
                            fontWeight: 700,
                            cursor:
                              canMark && !isSaving ? "pointer" : "not-allowed",
                            transition: "background 0.2s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                          }}
                        >
                          {isSaving ? (
                            "Saving…"
                          ) : canMark ? (
                            <>
                              <Check size={14} /> Mark as watched &amp; save
                              summary
                            </>
                          ) : (
                            "Complete both requirements above"
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  color: "rgba(255,255,255,0.3)",
                  paddingTop: 40,
                }}
              >
                <AlertCircle size={36} style={{ margin: "0 auto 8px" }} />
                <p>Video unavailable</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   READING PANEL
   ─────────────────────────────────────────────────────────────────
   Engagement conditions (both must be met for EVERY item):
     1. User ticks the item's checkbox
     2. User writes a reflection of ≥ MIN_REFLECTION_WORDS words

   Once all items satisfy both conditions a "Mark as complete"
   button activates.  logView fires on that button click only.
   Ticks + reflections are persisted in localStorage.
───────────────────────────────────────────────────────────────── */

interface ReadingPanelProps {
  item: LibraryContent;
  alreadyDone: boolean;
  onComplete: () => void;
  onClose: () => void;
}

const ReadingPanel: React.FC<ReadingPanelProps> = ({
  item,
  alreadyDone,
  onComplete,
  onClose,
}) => {
  const totalItems = item.readingItems?.length ?? 0;

  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    lsGet(LS_READING_CHECKS(item.id), {}),
  );
  const [reflections, setReflections] = useState<Record<string, string>>(() =>
    lsGet(LS_READING_REFLECTIONS(item.id), {}),
  );
  const [openReflection, setOpenReflection] = useState<string | null>(null);
  const [completed, setCompleted] = useState(alreadyDone);

  const toggleCheck = (id: string) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      lsSet(LS_READING_CHECKS(item.id), next);
      return next;
    });
  };

  const setReflection = (id: string, val: string) => {
    setReflections((prev) => {
      const next = { ...prev, [id]: val };
      lsSet(LS_READING_REFLECTIONS(item.id), next);
      return next;
    });
  };

  const reflectionOk = (id: string) =>
    wordCount(reflections[id] ?? "") >= MIN_REFLECTION_WORDS;

  const allChecked =
    totalItems > 0 &&
    (item.readingItems ?? []).every((ri) => !!checked[ri._id]);

  const allReflections =
    totalItems > 0 &&
    (item.readingItems ?? []).every((ri) => reflectionOk(ri._id));

  const canComplete = allChecked && allReflections && !completed;

  const handleComplete = () => {
    setCompleted(true);
    onComplete();
  };

  const readCount = (item.readingItems ?? []).filter(
    (ri) => !!checked[ri._id],
  ).length;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ zIndex: 9998 }}
    >
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 px-5 py-4 border-b sticky top-0 bg-white z-10 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <TypeChip type="reading" small />
            <p className="text-sm font-bold text-gray-900 mt-1 truncate">
              {item.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Progress strip */}
        {totalItems > 0 && !completed && (
          <div className="shrink-0 px-5 py-3 bg-purple-50 border-b border-purple-100">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold text-purple-700">
                Reading progress
              </p>
              <p className="text-xs font-bold text-purple-700">
                {readCount}/{totalItems} ticked
              </p>
            </div>
            <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 rounded-full transition-all duration-500"
                style={{
                  width:
                    totalItems > 0
                      ? `${(readCount / totalItems) * 100}%`
                      : "0%",
                }}
              />
            </div>
            <p className="text-[11px] text-purple-600 mt-1.5">
              Tick every item AND write a {MIN_REFLECTION_WORDS}-word reflection
              per item to complete.
            </p>
          </div>
        )}

        {completed && (
          <div className="shrink-0 px-5 py-3 bg-green-50 border-b border-green-100 flex items-center gap-2">
            <Check size={14} className="text-green-600 shrink-0" />
            <p className="text-xs font-semibold text-green-700">
              Reading complete — all items read and reflected on.
            </p>
          </div>
        )}

        {/* Description */}
        {item.description && (
          <div className="shrink-0 px-5 py-3 border-b">
            <p className="text-xs text-gray-500 leading-relaxed">
              {item.description}
            </p>
          </div>
        )}

        {/* Reading items */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {totalItems === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">
              No reading items added yet.
            </p>
          )}

          {(item.readingItems ?? []).map((ri) => {
            const done = !!checked[ri._id];
            const wc = wordCount(reflections[ri._id] ?? "");
            const refOk = wc >= MIN_REFLECTION_WORDS;
            const isOpen = openReflection === ri._id;
            const bothOk = done && refOk;

            return (
              <div
                key={ri._id}
                className={`rounded-xl border transition-all duration-150
                  ${bothOk ? "bg-green-50 border-green-200" : done ? "bg-purple-50 border-purple-200" : "bg-white border-gray-100"}`}
              >
                {/* Item header */}
                <div className="flex items-start gap-3 p-4">
                  {/* Tick */}
                  <button
                    onClick={() => toggleCheck(ri._id)}
                    className={`shrink-0 mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                      ${done ? "bg-purple-500 border-purple-500" : "border-gray-300 hover:border-purple-400"}`}
                  >
                    {done && <Check size={10} className="text-white" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    {ri.isPrimary && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full mb-1">
                        <Star size={8} /> Primary pick
                      </span>
                    )}
                    <p
                      className={`text-sm font-semibold leading-snug ${done && !bothOk ? "text-gray-500" : "text-gray-900"}`}
                    >
                      {ri.title}
                    </p>
                    {ri.author && (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {ri.author}
                      </p>
                    )}
                    {ri.description && (
                      <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                        {ri.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px] font-semibold text-gray-400 capitalize">
                        {ri.type}
                      </span>
                      {ri.externalUrl && (
                        <a
                          href={ri.externalUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600 hover:text-green-700 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={10} /> Get it
                        </a>
                      )}
                      {/* Reflection toggle */}
                      <button
                        onClick={() =>
                          setOpenReflection(isOpen ? null : ri._id)
                        }
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold transition-colors
                          ${refOk ? "text-green-600" : "text-purple-600 hover:text-purple-700"}`}
                      >
                        <MessageSquare size={10} />
                        {refOk
                          ? "Reflection ✓"
                          : `Add reflection (${wc}/${MIN_REFLECTION_WORDS} words)`}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reflection textarea — visible when toggled open */}
                {isOpen && (
                  <div className="px-4 pb-4">
                    <div
                      className={`rounded-lg border p-3 ${refOk ? "border-green-200 bg-green-50" : "border-purple-100 bg-purple-50/50"}`}
                    >
                      <p className="text-[11px] font-semibold text-gray-600 mb-2 flex items-center gap-1">
                        <MessageSquare size={10} />
                        What did you learn from "{ri.title}"? (min{" "}
                        {MIN_REFLECTION_WORDS} words)
                      </p>
                      <textarea
                        rows={4}
                        placeholder="Summarise the key insights, themes, or lessons in your own words…"
                        value={reflections[ri._id] ?? ""}
                        onChange={(e) => setReflection(ri._id, e.target.value)}
                        className="w-full text-xs text-gray-700 bg-transparent resize-none outline-none placeholder-gray-400 leading-relaxed"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <p
                          className={`text-[10px] font-semibold ${refOk ? "text-green-600" : wc > 0 ? "text-amber-600" : "text-gray-400"}`}
                        >
                          {wc} / {MIN_REFLECTION_WORDS} words {refOk ? "✓" : ""}
                        </p>
                        {refOk && (
                          <button
                            onClick={() => setOpenReflection(null)}
                            className="text-[11px] font-semibold text-green-600 hover:text-green-700"
                          >
                            Save ✓
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Completion checklist + button */}
          {totalItems > 0 && !completed && (
            <div
              className={`rounded-xl p-4 border mt-2 transition-all ${canComplete ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-100"}`}
            >
              <p className="text-xs font-semibold text-gray-700 mb-2">
                Completion checklist
              </p>
              <div className="space-y-1.5 mb-4">
                {[
                  {
                    done: allChecked,
                    label: `All ${totalItems} items ticked as read`,
                  },
                  {
                    done: allReflections,
                    label: `All reflections written (≥ ${MIN_REFLECTION_WORDS} words each)`,
                  },
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span
                      className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${c.done ? "bg-green-500" : "bg-gray-200"}`}
                    >
                      {c.done && <Check size={9} className="text-white" />}
                    </span>
                    <span
                      className={
                        c.done ? "text-green-700 font-medium" : "text-gray-500"
                      }
                    >
                      {c.label}
                    </span>
                  </div>
                ))}
              </div>
              <button
                disabled={!canComplete}
                onClick={handleComplete}
                className={`w-full py-2.5 text-sm font-semibold rounded-xl transition-all
                  ${
                    canComplete
                      ? "bg-green-600 text-white hover:bg-green-700 active:scale-95 shadow-sm"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {canComplete
                  ? "Mark reading as complete ✓"
                  : "Complete all items above first"}
              </button>
            </div>
          )}

          {completed && (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-center mt-2">
              <p className="text-sm font-bold text-green-700">
                Reading complete ✓
              </p>
              <p className="text-[11px] text-green-600 mt-1">
                Your reflections are saved locally and your engagement has been
                logged.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   DOCUMENT CONFIRM DIALOG
   ─────────────────────────────────────────────────────────────────
   For resource and task-brief items.
   Step 1: user opens the document link (enables step 2)
   Step 2: user explicitly confirms they have read it in full
   logView fires on step-2 confirmation only.
───────────────────────────────────────────────────────────────── */

interface DocConfirmProps {
  item: LibraryContent;
  alreadyDone: boolean;
  onComplete: () => void;
  onClose: () => void;
}

const DocConfirmDialog: React.FC<DocConfirmProps> = ({
  item,
  alreadyDone,
  onComplete,
  onClose,
}) => {
  const [opened, setOpened] = useState(false);
  const [confirmed, setConfirmed] = useState(alreadyDone);

  const handleOpenFile = () => {
    if (item.fileUrl) window.open(item.fileUrl, "_blank", "noreferrer");
    setOpened(true);
  };

  const handleConfirm = () => {
    setConfirmed(true);
    onComplete();
  };

  const meta = TYPE_META[item.contentType];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/50"
      style={{ zIndex: 9998 }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.bg}`}
          >
            {meta.icon}
          </div>
          <div className="flex-1 min-w-0">
            <TypeChip type={item.contentType} small />
            <p className="text-sm font-bold text-gray-900 mt-1 truncate">
              {item.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {item.description && (
          <p className="text-xs text-gray-500 leading-relaxed px-5 pb-3">
            {item.description}
          </p>
        )}

        <div className="px-5 pb-5 space-y-3">
          {confirmed ? (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-center">
              <p className="text-sm font-bold text-green-700">
                Marked as read ✓
              </p>
              <p className="text-[11px] text-green-600 mt-1">
                Your engagement with this document has been logged.
              </p>
              <button
                onClick={onClose}
                className="mt-3 text-xs font-semibold text-green-600 hover:text-green-700"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Step 1 */}
              <div
                className={`rounded-xl border p-4 transition-all ${opened ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0
                    ${opened ? "bg-green-500 text-white" : "bg-gray-300 text-gray-600"}`}
                  >
                    {opened ? <Check size={10} /> : "1"}
                  </span>
                  <p className="text-xs font-semibold text-gray-700">
                    Open and read the document
                  </p>
                </div>
                <button
                  onClick={handleOpenFile}
                  className="w-full py-2 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 active:scale-95 transition-all flex items-center justify-center gap-2"
                >
                  <ExternalLink size={13} />
                  {opened ? "Re-open document" : "Open document"}
                </button>
                {item.isDownloadable && item.fileUrl && (
                  <a
                    href={item.fileUrl}
                    download={item.fileName || true}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 w-full py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Download size={12} /> Download instead
                  </a>
                )}
              </div>

              {/* Step 2 */}
              <div
                className={`rounded-xl border p-4 transition-all ${opened ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-100 opacity-50 pointer-events-none"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0
                    ${opened ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-500"}`}
                  >
                    2
                  </span>
                  <p className="text-xs font-semibold text-gray-700">
                    Confirm you have read it in full
                  </p>
                </div>
                <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">
                  Only confirm once you have read and understood the entire
                  document. This will be logged as your engagement with this
                  resource.
                </p>
                <button
                  disabled={!opened}
                  onClick={handleConfirm}
                  className={`w-full py-2.5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 transition-all
                    ${
                      opened
                        ? "bg-green-600 text-white hover:bg-green-700 active:scale-95 shadow-sm"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <Check size={14} /> I have read this document in full
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   LIBRARY CARD
───────────────────────────────────────────────────────────────── */

interface CardProps {
  item: LibraryContent;
  isLocked: boolean;
  viewedIds: Set<string>;
  downloadedIds: Set<string>;
  taskStatus?: string;
  onOpen: (item: LibraryContent) => void;
  onDownload: (item: LibraryContent) => void;
}

const LibraryCard: React.FC<CardProps> = ({
  item,
  isLocked,
  viewedIds,
  downloadedIds,
  taskStatus,
  onOpen,
  onDownload,
}) => {
  const meta = TYPE_META[item.contentType];
  const hasViewed = viewedIds.has(item.id);
  const hasDown = downloadedIds.has(item.id);
  const videoId = extractYouTubeId(item.videoUrl);
  const thumb = videoId
    ? (item.thumbnailUrl ??
      `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`)
    : null;
  const taskMeta = taskStatus
    ? TASK_STATUS_META[taskStatus as keyof typeof TASK_STATUS_META]
    : null;

  // Video in-progress state (saved to localStorage)
  const videoPct = videoId
    ? Math.round(lsGet<number>(LS_VIDEO_PROGRESS(item.id), 0) * 100)
    : 0;
  const videoInProgress = !hasViewed && videoPct > 0;

  // Reading in-progress state
  const readChecks =
    item.contentType === "reading"
      ? lsGet<Record<string, boolean>>(LS_READING_CHECKS(item.id), {})
      : {};
  const readingPct =
    item.contentType === "reading" && (item.readingItems?.length ?? 0) > 0
      ? Math.round(
          (Object.values(readChecks).filter(Boolean).length /
            item.readingItems!.length) *
            100,
        )
      : 0;
  const readingInProgress = !hasViewed && readingPct > 0;

  return (
    <div
      className={`group relative bg-white rounded-2xl border shadow-sm flex flex-col transition-all duration-200
        ${
          isLocked
            ? "border-gray-100 opacity-60 cursor-not-allowed"
            : "border-gray-100 hover:border-green-200 hover:shadow-md cursor-pointer"
        }`}
      onClick={() => !isLocked && onOpen(item)}
    >
      {/* Video thumbnail */}
      {thumb && !isLocked ? (
        <div
          className="relative w-full rounded-t-2xl overflow-hidden"
          style={{ paddingBottom: "52%" }}
        >
          <img
            src={thumb}
            alt={item.title}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
              <Play size={16} className="text-gray-900 ml-0.5" />
            </div>
          </div>

          {/* Engagement badge on thumbnail */}
          <div className="absolute top-2 right-2">
            {hasViewed ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-600 text-white">
                <Check size={9} /> Watched ✓
              </span>
            ) : videoInProgress ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/70 text-white">
                {videoPct}% watched
              </span>
            ) : null}
          </div>

          {item.durationLabel && (
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
              {item.durationLabel}
            </div>
          )}

          {/* Video progress underline */}
          {videoInProgress && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
              <div
                className="h-full bg-indigo-400"
                style={{ width: `${videoPct}%` }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={`h-1.5 w-full rounded-t-2xl ${meta.accentBar}`} />
      )}

      <div className="p-4 flex flex-col flex-1 gap-2.5">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <TypeChip type={item.contentType} />
          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            {item.isNew && !isLocked && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-500 text-white uppercase tracking-wide">
                New
              </span>
            )}
            {isLocked && <Lock size={12} className="text-gray-400" />}
            {/* Non-video engagement badges */}
            {hasViewed && !thumb && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                <Check size={9} /> Completed ✓
              </span>
            )}
            {readingInProgress && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">
                {readingPct}% read
              </span>
            )}
            {hasDown && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                <Download size={9} /> Downloaded
              </span>
            )}
          </div>
        </div>

        {/* Task submission status */}
        {taskMeta && item.contentType === "task" && (
          <div
            className={`flex items-center gap-1.5 text-[11px] font-semibold ${taskMeta.colour}`}
          >
            <span className={`w-2 h-2 rounded-full shrink-0 ${taskMeta.dot}`} />
            Task {taskMeta.label}
          </div>
        )}

        {/* Title */}
        <div className="flex-1">
          <h3
            className={`text-sm font-semibold leading-snug mb-1
            ${isLocked ? "text-gray-400" : "text-gray-900 group-hover:text-green-700 transition-colors"}`}
          >
            {item.title}
          </h3>
          {item.description && (
            <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
              {item.description}
            </p>
          )}
        </div>

        {/* Reading items preview */}
        {item.contentType === "reading" &&
          (item.readingItems?.length ?? 0) > 0 && (
            <div className="flex items-center gap-1.5 text-[11px] text-purple-600 font-medium">
              <BookOpen size={11} />
              {item.readingItems!.length} items
              {readingInProgress && (
                <span className="text-gray-400">· {readingPct}% read</span>
              )}
              {hasViewed && (
                <span className="text-green-600">· All complete ✓</span>
              )}
              {!readingInProgress && !hasViewed && (
                <span className="text-gray-400">— tap to open</span>
              )}
            </div>
          )}

        {/* Tags */}
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 4).map((t) => (
              <span
                key={t}
                className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-50">
          <div className="flex items-center gap-2 text-[11px] text-gray-400">
            {item.durationLabel && !thumb && (
              <span className="flex items-center gap-1">
                <Clock size={10} />
                {item.durationLabel}
              </span>
            )}
            {item.publishedAt && <span>{fmtDate(item.publishedAt)}</span>}
          </div>

          {!isLocked && (
            <div
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {item.isDownloadable && item.fileUrl && (
                <a
                  href={item.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  download={item.fileName || true}
                  onClick={() => onDownload(item)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                  title={item.fileName ?? "Download"}
                >
                  <Download size={13} />
                </a>
              )}
              <button
                onClick={() => onOpen(item)}
                className="flex items-center gap-1 text-[11px] font-semibold text-green-600 hover:text-green-700 transition-colors"
              >
                {item.contentType === "keynote" || item.contentType === "panel"
                  ? hasViewed
                    ? "Re-watch"
                    : videoInProgress
                      ? "Continue"
                      : "Watch"
                  : item.contentType === "reading"
                    ? hasViewed
                      ? "Review"
                      : readingInProgress
                        ? "Continue reading"
                        : "Open list"
                    : hasViewed
                      ? "Re-open"
                      : "Open"}
                <ChevronRight size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   QUARTER SELECTOR CARD  (unchanged from user's version)
───────────────────────────────────────────────────────────────── */

const QuarterCard: React.FC<{
  summary: QuarterSummaryItem;
  isSelected: boolean;
  itemCount: number;
  viewedCount: number;
  onClick: () => void;
}> = ({ summary, isSelected, itemCount, viewedCount, onClick }) => {
  const status = summary.status;

  const borderCls =
    status === "locked"
      ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-50"
      : isSelected && status === "active"
        ? "border-green-400 bg-green-50"
        : isSelected && status === "completed"
          ? "border-purple-400 bg-purple-50"
          : isSelected
            ? "border-gray-400 bg-gray-50"
            : status === "active"
              ? "border-gray-200 hover:border-green-300 bg-white"
              : status === "completed"
                ? "border-gray-200 hover:border-purple-300 bg-white"
                : "border-gray-200 hover:border-gray-300 bg-white";

  const engagePct =
    itemCount > 0 ? Math.round((viewedCount / itemCount) * 100) : 0;

  const statusBadge = {
    completed: { text: "Completed", cls: "bg-purple-100 text-purple-700" },
    active: { text: "Active", cls: "bg-green-100 text-green-700" },
    upcoming: { text: "Upcoming", cls: "bg-gray-100 text-gray-500" },
    locked: { text: "Locked", cls: "bg-gray-100 text-gray-400" },
  }[status];

  return (
    <button
      onClick={onClick}
      disabled={status === "locked"}
      className={`relative text-left rounded-xl border p-4 transition-all duration-150 ${borderCls}`}
    >
      <div className="flex items-start justify-between gap-1 mb-2">
        <span
          className={`text-lg font-bold ${isSelected ? "text-gray-900" : "text-gray-600"}`}
        >
          {summary.quarter}
        </span>
        <span
          className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${statusBadge.cls}`}
        >
          {statusBadge.text}
        </span>
      </div>
      <p className="text-xs text-gray-500 leading-snug line-clamp-2 mb-2">
        {summary.theme ?? "Theme TBD"}
      </p>
      {(status === "active" || status === "completed") && itemCount > 0 && (
        <div>
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            <span>
              {viewedCount}/{itemCount} completed
            </span>
            <span>{engagePct}%</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${engagePct === 100 ? "bg-purple-500" : "bg-green-400"}`}
              style={{ width: `${engagePct}%` }}
            />
          </div>
        </div>
      )}
      {status === "locked" && (
        <Lock size={12} className="text-gray-400 absolute bottom-3 right-3" />
      )}
      {isSelected && status !== "locked" && (
        <span className="absolute top-3 right-3 w-1.5 h-1.5 rounded-full bg-green-500" />
      )}
    </button>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */

const Library: React.FC = () => {
  const { data: myQuarterRes, isLoading: qLoading } = useGetMyQuarterQuery();
  const myQuarterData = myQuarterRes?.data;

  const [activeQuarter, setActiveQuarter] = useState<QuarterKey | null>(null);
  useEffect(() => {
    if (myQuarterData && !activeQuarter)
      setActiveQuarter(myQuarterData.currentQuarter);
  }, [myQuarterData, activeQuarter]);

  const quarterSummaries: QuarterSummaryItem[] =
    myQuarterData?.quarterSummary ?? [];
  const currentQ = activeQuarter ?? myQuarterData?.currentQuarter ?? "Q1";
  const activeSummary = quarterSummaries.find((q) => q.quarter === currentQ);
  const isLocked = activeSummary?.status === "locked";
  const isUpcoming = activeSummary?.status === "upcoming";

  const [typeFilter, setTypeFilter] = useState<ContentType | "all">("all");
  const [search, setSearch] = useState("");

  const { data: libraryRes, isLoading: libLoading } = useGetLibraryContentQuery(
    { quarter: currentQ },
    { skip: isLocked },
  );

  const { data: viewedActRes } = useGetActivityQuery(
    { quarter: currentQ, type: "content_viewed", limit: 100 },
    { skip: isLocked },
  );
  const { data: downloadActRes } = useGetActivityQuery(
    { quarter: currentQ, type: "content_downloaded", limit: 100 },
    { skip: isLocked },
  );
  const { data: journeyQRes } = useGetJourneyQuartersQuery();
  const { data: tasksRes } = useGetTasksQuery(
    { quarter: currentQ },
    { skip: isLocked },
  );

  const [logView] = useLogViewMutation();
  const [logDownload] = useLogDownloadMutation();

  // Overlay state
  const [videoItem, setVideoItem] = useState<LibraryContent | null>(null);
  const [readingItem, setReadingItem] = useState<LibraryContent | null>(null);
  const [docItem, setDocItem] = useState<LibraryContent | null>(null);

  // Optimistic local set — updated immediately on completion so the badge
  // updates without waiting for the activity query to refetch
  const [localViewed, setLocalViewed] = useState<Set<string>>(new Set());

  const viewedIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>(Array.from(localViewed));
    (viewedActRes?.data ?? []).forEach((a: ActivityItem) => {
      if (a.libraryContent) ids.add(a.libraryContent);
    });
    return ids;
  }, [viewedActRes, localViewed]);

  const downloadedIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    (downloadActRes?.data ?? []).forEach((a: ActivityItem) => {
      if (a.libraryContent) ids.add(a.libraryContent);
    });
    return ids;
  }, [downloadActRes]);

  const journeyQMap = useMemo(() => {
    const m: Record<string, number> = {};
    (journeyQRes?.data ?? []).forEach((q) => {
      m[q.quarter] = q.contentViewed;
    });
    return m;
  }, [journeyQRes]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allItems = libraryRes?.data ?? [];

  const filtered = useMemo(() => {
    let items = allItems;
    if (typeFilter !== "all")
      items = items.filter((i) => i.contentType === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.description?.toLowerCase().includes(q) ||
          i.tags?.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return items;
  }, [allItems, typeFilter, search]);

  const engagedThisQ = allItems.filter(
    (i) => viewedIds.has(i.id) || downloadedIds.has(i.id),
  ).length;
  const totalThisQ = allItems.length;
  const engagedPct =
    totalThisQ > 0 ? Math.round((engagedThisQ / totalThisQ) * 100) : 0;

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: allItems.length };
    allItems.forEach((i) => {
      counts[i.contentType] = (counts[i.contentType] ?? 0) + 1;
    });
    return counts;
  }, [allItems]);

  // Called by any overlay when its engagement condition is fully met
  const handleComplete = useCallback(
    (item: LibraryContent) => {
      logView(item.id).catch(() => {});
      setLocalViewed(
        (prev) => new Set<string>(Array.from(prev).concat(item.id)),
      );
      // Close the overlay that just completed
      setVideoItem(null);
      setReadingItem(null);
      setDocItem(null);
    },
    [logView],
  );

  const handleOpen = useCallback((item: LibraryContent) => {
    if (item.contentType === "keynote" || item.contentType === "panel") {
      setVideoItem(item);
    } else if (item.contentType === "reading") {
      setReadingItem(item);
    } else {
      // resource and task brief — two-step document confirmation
      setDocItem(item);
    }
  }, []);

  const handleDownload = useCallback(
    (item: LibraryContent) => {
      logDownload(item.id).catch(() => {});
    },
    [logDownload],
  );

  const handleQuarterChange = (q: QuarterKey) => {
    setActiveQuarter(q);
    setTypeFilter("all");
    setSearch("");
  };

  const ALL_TYPES: ContentType[] = [
    "keynote",
    "panel",
    "reading",
    "resource",
    "task",
  ];

  /* ──────────────────────────────────────────────────────────────── */
  return (
    <>
      <div className="space-y-7 pb-12">
        {/* PAGE HEADER */}
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-green-600 mb-1">
            Mentorship Portal
          </p>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
            Library
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Every keynote, panel, reading list, and resource — organised by
            quarter and available 24/7.
          </p>
        </div>

        {/* QUARTER SELECTOR */}
        {qLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Bone key={i} className="h-28" />
            ))}
          </div>
        ) : (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">
              Select quarter
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {quarterSummaries.map((qs) => (
                <QuarterCard
                  key={qs.quarter}
                  summary={qs}
                  isSelected={currentQ === qs.quarter}
                  itemCount={qs.quarter === currentQ ? totalThisQ : 0}
                  viewedCount={journeyQMap[qs.quarter] ?? 0}
                  onClick={() => handleQuarterChange(qs.quarter)}
                />
              ))}
            </div>
          </div>
        )}

        {/* ACTIVE QUARTER SUMMARY BAR */}
        {!qLoading && activeSummary && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Viewing</p>
              <h2 className="text-sm font-semibold text-gray-900">
                {activeSummary.quarter}
                {activeSummary.theme ? ` — ${activeSummary.theme}` : ""}
              </h2>
              {activeSummary.tagline && (
                <p className="text-[11px] text-gray-400 italic mt-0.5">
                  {activeSummary.tagline}
                </p>
              )}
            </div>
            {!isLocked && !isUpcoming && totalThisQ > 0 && (
              <div className="shrink-0 flex flex-col items-end gap-1.5">
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <FileText size={11} />
                    {totalThisQ} items
                  </span>
                  <span
                    className={`flex items-center gap-1 font-semibold ${
                      engagedPct === 100
                        ? "text-purple-600"
                        : engagedPct > 0
                          ? "text-green-600"
                          : "text-gray-400"
                    }`}
                  >
                    <Eye size={11} />
                    {engagedThisQ} completed
                  </span>
                </div>
                <div className="flex items-center gap-2 w-40">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${engagedPct === 100 ? "bg-purple-500" : "bg-green-400"}`}
                      style={{ width: `${engagedPct}%` }}
                    />
                  </div>
                  <span className="text-[10px] font-bold text-gray-400">
                    {engagedPct}%
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* LOCKED */}
        {isLocked && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <Lock size={24} className="text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-700 mb-1">
              {currentQ} content is not yet available
            </h3>
            <p className="text-sm text-gray-400 max-w-sm">
              This quarter's library will be unlocked when you reach {currentQ}{" "}
              in your personal programme journey.
            </p>
          </div>
        )}

        {/* UPCOMING */}
        {isUpcoming && !isLocked && (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mb-2">
              <Clock size={24} className="text-amber-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-700">
              {currentQ} is coming up next
            </h3>
            {activeSummary?.theme && (
              <p className="text-sm font-medium text-gray-600">
                "{activeSummary.theme}"
              </p>
            )}
            <p className="text-sm text-gray-400 max-w-sm">
              Content will be available once you advance to {currentQ}. Keep
              engaging with your current quarter to progress.
            </p>
            {activeSummary?.eventDate && (
              <p className="text-xs font-semibold text-green-600 flex items-center gap-1">
                <Zap size={12} /> Event: {fmtDate(activeSummary.eventDate)}
              </p>
            )}
          </div>
        )}

        {/* ACTIVE CONTENT */}
        {!isLocked && !isUpcoming && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="Search titles, descriptions, tags…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-transparent placeholder-gray-400 transition"
                />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {(["all", ...ALL_TYPES] as ("all" | ContentType)[]).map((t) => {
                  const count = typeCounts[t] ?? 0;
                  const isActive = typeFilter === t;
                  const label = t === "all" ? "All" : TYPE_META[t].label;
                  return (
                    <button
                      key={t}
                      onClick={() => setTypeFilter(t as any)}
                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all
                        ${
                          isActive
                            ? "bg-green-600 text-white border-green-600 shadow-sm"
                            : "bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:text-green-700"
                        }`}
                    >
                      {label}
                      <span
                        className={`text-[10px] font-bold px-1 py-0.5 rounded ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Engagement requirements info box */}
            {!libLoading && allItems.length > 0 && (
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                <Info size={14} className="text-blue-500 shrink-0 mt-0.5" />
                <div className="text-[11px] text-blue-700 space-y-0.5">
                  <p className="font-semibold text-blue-800 text-xs">
                    How engagement is counted
                  </p>
                  <p>
                    🎬 <strong>Videos</strong> — must watch at least{" "}
                    {Math.round(VIDEO_COMPLETION_THRESHOLD * 100)}% of the full
                    duration
                  </p>
                  <p>
                    📚 <strong>Reading lists</strong> — tick every item as read
                    + write a {MIN_REFLECTION_WORDS}-word reflection per item
                  </p>
                  <p>
                    📄 <strong>Documents & task briefs</strong> — open the
                    document, then confirm you have read it in full
                  </p>
                </div>
              </div>
            )}

            {/* Engagement nudge */}
            {!libLoading && allItems.length > 0 && engagedThisQ === 0 && (
              <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                <TrendingUp size={16} className="text-green-600 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-green-800">
                    Start engaging with this quarter's content
                  </p>
                  <p className="text-[11px] text-green-600 mt-0.5">
                    Content engagement counts towards your Legacy Fellows
                    eligibility assessment.
                  </p>
                </div>
              </div>
            )}

            {/* Grid */}
            {libLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <Bone key={i} className="h-56" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-4">
                  <Search size={22} className="text-gray-300" />
                </div>
                <h3 className="text-base font-semibold text-gray-700 mb-1">
                  No results found
                </h3>
                <p className="text-sm text-gray-400">
                  Try a different search term or clear the filter.
                </p>
                <button
                  onClick={() => {
                    setSearch("");
                    setTypeFilter("all");
                  }}
                  className="mt-4 text-xs font-semibold text-green-600 hover:text-green-700 underline underline-offset-2"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                    {typeFilter === "all"
                      ? "All content"
                      : TYPE_META[typeFilter].label + "s"}{" "}
                    · {currentQ}
                  </p>
                  <span className="text-xs text-gray-400">
                    {filtered.length} item{filtered.length !== 1 ? "s" : ""}
                    {engagedThisQ > 0 && ` · ${engagedThisQ} completed`}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filtered.map((item) => {
                    let taskStatus: string | undefined;
                    if (item.contentType === "task") {
                      const matchedTask = (tasksRes?.data ?? []).find(
                        (t) =>
                          t.quarter === item.quarter &&
                          (item.title.includes(`Task ${t.taskNumber}`) ||
                            item.title
                              .toLowerCase()
                              .includes(t.title?.toLowerCase().split(" ")[0])),
                      );
                      taskStatus = matchedTask?.submission?.status;
                    }
                    return (
                      <LibraryCard
                        key={item.id}
                        item={item}
                        isLocked={false}
                        viewedIds={viewedIds}
                        downloadedIds={downloadedIds}
                        taskStatus={taskStatus}
                        onOpen={handleOpen}
                        onDownload={handleDownload}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* All-complete celebration */}
            {!libLoading && totalThisQ > 0 && engagedPct === 100 && (
              <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-purple-700 p-5 text-white text-center">
                <p className="text-lg font-bold mb-1">
                  🎉 You've completed all {currentQ} content!
                </p>
                <p className="text-sm text-purple-200">
                  Your full engagement — videos watched, reading reflected on,
                  and documents confirmed — has been recorded.
                </p>
              </div>
            )}
          </>
        )}

        {/* Upcoming quarter teaser */}
        {!isLocked &&
          !isUpcoming &&
          (() => {
            const nextQ = myQuarterData?.nextQuarter;
            const nextSummary = quarterSummaries.find(
              (q) => q.quarter === nextQ,
            );
            if (!nextQ || !nextSummary || nextSummary.status === "locked")
              return null;
            return (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                    Coming Next
                  </p>
                  <h3 className="text-sm font-semibold text-gray-700">
                    {nextQ}
                    {nextSummary.theme ? ` · ${nextSummary.theme}` : ""}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Content will be available when you advance to {nextQ}.
                  </p>
                </div>
                <button
                  onClick={() => handleQuarterChange(nextQ)}
                  className="shrink-0 inline-flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-green-700 border border-gray-200 hover:border-green-300 rounded-lg px-4 py-2 bg-white transition-colors"
                >
                  Preview {nextQ} <ChevronRight size={13} />
                </button>
              </div>
            );
          })()}
      </div>

      {/* ── OVERLAYS — outside the scroll container ────────────────── */}

      {videoItem && (
        <VideoModal
          item={videoItem}
          alreadyDone={viewedIds.has(videoItem.id)}
          onComplete={() => handleComplete(videoItem)}
          onClose={() => setVideoItem(null)}
        />
      )}

      {readingItem && (
        <ReadingPanel
          item={readingItem}
          alreadyDone={viewedIds.has(readingItem.id)}
          onComplete={() => handleComplete(readingItem)}
          onClose={() => setReadingItem(null)}
        />
      )}

      {docItem && (
        <DocConfirmDialog
          item={docItem}
          alreadyDone={viewedIds.has(docItem.id)}
          onComplete={() => handleComplete(docItem)}
          onClose={() => setDocItem(null)}
        />
      )}
    </>
  );
};

export default Library;
